import * as React from "react";

import { logger } from "@/lib/default-logger";

export interface AudioPreview {
	/** Id of the item currently playing, or null. */
	playingId: string | null;
	/** Play the preview for `id`, or pause it when it is already playing. */
	toggle: (id: string, url: string) => void;
	stop: () => void;
}

// One shared HTMLAudioElement for short audio previews (voice samples etc.).
// Starting a preview stops the previous one; unmount stops playback.
export function useAudioPreview(): AudioPreview {
	const audioRef = React.useRef<HTMLAudioElement | null>(null);
	const [playingId, setPlayingId] = React.useState<string | null>(null);

	const stop = React.useCallback(() => {
		if (audioRef.current) {
			audioRef.current.pause();
			audioRef.current = null;
		}
		setPlayingId(null);
	}, []);

	React.useEffect(() => stop, [stop]);

	const toggle = React.useCallback(
		(id: string, url: string) => {
			if (playingId === id) {
				stop();
				return;
			}

			stop();

			const audio = new Audio(url);
			audio.addEventListener("ended", () => {
				setPlayingId(null);
				audioRef.current = null;
			});
			audio.play().catch((error: unknown) => {
				logger.error(error);
				setPlayingId(null);
				audioRef.current = null;
			});

			audioRef.current = audio;
			setPlayingId(id);
		},
		[playingId, stop]
	);

	return { playingId, toggle, stop };
}
